import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { AdminNav } from "../../components/admin/AdminNav";
import { FileUpload } from "../../components/admin/FileUpload";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="overline mb-1 block">{label}</label>
      <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="editorial-input" />
    </div>
  );
}

function Area({ label, value, onChange, placeholder, rows = 3, testid, hint }) {
  return (
    <div>
      <label className="overline mb-1 block">{label}</label>
      <textarea data-testid={testid} rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="editorial-input resize-none" />
      {hint && <p className="text-xs text-[#5C6678] mt-1">{hint}</p>}
    </div>
  );
}

export default function SiteSettingsPage() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => toast.error("Errore nel caricamento"));
  }, []);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      toast.success("Impostazioni salvate");
    } catch {
      toast.error("Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (!s) {
    return (
      <div className="min-h-screen bg-[#F4F8FF]">
        <AdminNav />
        <div className="py-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#1452F0]" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F8FF] pb-28">
      <AdminNav />
      <div className="px-6 md:px-10 max-w-[1000px] mx-auto py-8">
        <h1 className="font-heading text-4xl font-light text-[#0B1220] mb-2">Sito</h1>
        <p className="text-[#5C6678] mb-8">Modifica foto e testi della homepage e del footer.</p>

        {/* HOMEPAGE */}
        <section className="bg-white border border-[#D5DEEE] rounded-sm p-6 md:p-8 space-y-6">
          <h2 className="font-heading text-2xl text-[#0B1220]">Homepage</h2>
          <FileUpload label="Foto hero homepage" value={s.home_hero_image} onChange={(v) => set("home_hero_image", v)} testid="set-home-image" height="h-56" />
          <Field label="Soprattitolo (overline)" value={s.home_overline} onChange={(v) => set("home_overline", v)} testid="set-home-overline" />
          <Area label="Titolo principale" value={s.home_title} onChange={(v) => set("home_title", v)} rows={2} hint="Vai a capo per scrivere su più righe." testid="set-home-title" />
          <Area label="Sottotitolo" value={s.home_subtitle} onChange={(v) => set("home_subtitle", v)} testid="set-home-subtitle" />
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Pulsante primario" value={s.home_cta_primary} onChange={(v) => set("home_cta_primary", v)} testid="set-home-cta1" />
            <Field label="Pulsante secondario" value={s.home_cta_secondary} onChange={(v) => set("home_cta_secondary", v)} testid="set-home-cta2" />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Sezione corsi — soprattitolo" value={s.home_courses_overline} onChange={(v) => set("home_courses_overline", v)} testid="set-home-coverline" />
            <Field label="Sezione corsi — titolo" value={s.home_courses_title} onChange={(v) => set("home_courses_title", v)} testid="set-home-ctitle" />
          </div>
          <Area label="Sezione corsi — nota" value={s.home_courses_note} onChange={(v) => set("home_courses_note", v)} rows={2} testid="set-home-cnote" />
        </section>

        {/* FOOTER */}
        <section className="bg-white border border-[#D5DEEE] rounded-sm p-6 md:p-8 space-y-6 mt-6">
          <h2 className="font-heading text-2xl text-[#0B1220]">Footer</h2>
          <Field label="Nome / brand" value={s.footer_brand} onChange={(v) => set("footer_brand", v)} testid="set-footer-brand" />
          <Area label="Descrizione" value={s.footer_description} onChange={(v) => set("footer_description", v)} testid="set-footer-desc" />
          <Field label="Dati societari (intestatario)" value={s.footer_company} onChange={(v) => set("footer_company", v)} testid="set-footer-company" />
          <Field label="IBAN" value={s.footer_iban} onChange={(v) => set("footer_iban", v)} testid="set-footer-iban" />
          <Field label="Testo copyright" value={s.footer_copyright} onChange={(v) => set("footer_copyright", v)} testid="set-footer-copyright" />
        </section>
      </div>

      <div className="fixed bottom-0 left-0 w-full bg-[#0B1220] z-30">
        <div className="px-6 md:px-10 max-w-[1000px] mx-auto py-4 flex items-center justify-between">
          <span className="text-sm text-white/60">Impostazioni del sito</span>
          <button data-testid="save-settings-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-white text-[#0B1220] hover:bg-[#5B9DFF] hover:text-white transition-colors rounded-sm px-8 py-3 text-sm font-medium disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Salva
          </button>
        </div>
      </div>
    </div>
  );
}
